import React from 'react';
import { SocketProvider, useSocket } from './contexts/SocketContext';
import { RoomCreation } from './components/RoomCreation';
import { RoomJoining } from './components/RoomJoining';
import { RoomLobby } from './components/RoomLobby';
import { RoomHostActionsMenu } from './components/RoomHostActionsMenu';
import { DeadpanRoleAssignment } from './components/deadpan/DeadpanRoleAssignment';
import { DeadpanVoting } from './components/deadpan/DeadpanVoting';
import { DeadpanRoleReveal } from './components/deadpan/DeadpanRoleReveal';
import { WordsmithWordInput } from './components/deadpan/WordsmithWordInput';
import { UndercoverRoleAssignment } from './components/undercover/UndercoverRoleAssignment';
import { UndercoverVoting } from './components/undercover/UndercoverVoting';
import { UndercoverRoleReveal } from './components/undercover/UndercoverRoleReveal';
import { UndercoverWordsmithWordInput } from './components/undercover/UndercoverWordsmithWordInput';
import { UndercoverGhostFactInput } from './components/undercover/UndercoverGhostFactInput';
import { SimilarMindsHostPrompt } from './components/similarMinds/SimilarMindsHostPrompt';
import { SimilarMindsAnswerInput } from './components/similarMinds/SimilarMindsAnswerInput';
import { SimilarMindsVoting } from './components/similarMinds/SimilarMindsVoting';
import { SimilarMindsResults } from './components/similarMinds/SimilarMindsResults';
import { AceAttorneyPrompt } from './components/aceAttorney/AceAttorneyPrompt';
import { AceAttorneyAnswers } from './components/aceAttorney/AceAttorneyAnswers';
import { AceAttorneyReveal } from './components/aceAttorney/AceAttorneyReveal';
import { BombRelayPlay } from './components/bombRelay/BombRelayPlay';
import { BombRelayResults } from './components/bombRelay/BombRelayResults';
import { PensieveGame } from './components/pensieve/PensieveGame';
import { GameTheoryWheelGame } from './components/gameTheoryWheel/GameTheoryWheelGame';
import { StandApartGame } from './components/standApart/StandApartGame';
import { SchrodingerCrocGame } from './components/schrodingerCroc/SchrodingerCrocGame';
import { PageWrapper } from './components/PageWrapper';
import { ToolsPanel } from './components/ToolsPanel';
import { WeekendHahaPanel } from './components/WeekendHahaPanel';
import { SEO } from './components/SEO';
import { VoiceChatSection } from './components/VoiceChatSection';
import { EmojiReactionSection } from './components/EmojiReactionSection';
import { GamePhase, GameType, isNashSchrodinger, isNashStandApart } from '@deadpan/shared';
import { useTranslations } from './hooks/useTranslations';
import { DeadpanItemPanel } from './components/deadpan/DeadpanItemPanel';
import { InviteJoinModal } from './components/InviteJoinModal';
import {
  parseInviteRoomCodeFromLocation,
  clearInviteFromAddressBar,
} from './utils/roomInvitePath';

function AppContent() {
  const { socket, currentRoom, gameState, myPlayerId } = useSocket();
  const t = useTranslations();
  const [showTools, setShowTools] = React.useState(false);
  const [showWeekendHaha, setShowWeekendHaha] = React.useState(false);
  const [inviteRoomCode, setInviteRoomCode] = React.useState<string | null>(() =>
    typeof window !== 'undefined' ? parseInviteRoomCodeFromLocation() : null,
  );

  React.useEffect(() => {
    const onHashChange = () => {
      if (currentRoom) return;
      setInviteRoomCode(parseInviteRoomCodeFromLocation());
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [currentRoom]);

  const dismissInviteModal = React.useCallback(() => {
    setInviteRoomCode(null);
    clearInviteFromAddressBar();
  }, []);

  // Get game type from room, default to DEADPAN
  const gameType = currentRoom?.gameType || GameType.DEADPAN;
  const myPlayer = currentRoom?.players.find((player) => player.id === myPlayerId);
  const isGameSpectator = Boolean(
    currentRoom && myPlayer?.isSpectator && currentRoom.phase !== GamePhase.WAITING,
  );
  const deadpanSidePanel = currentRoom && gameType === GameType.DEADPAN && currentRoom.phase !== GamePhase.WAITING && myPlayer?.deadpanItemSlot
    ? <DeadpanItemPanel />
    : undefined;

  const handleToolsOpen = () => {
    setShowTools(true);
    setShowWeekendHaha(false);
    if (socket) {
      socket.emit('client:open-tools-panel');
    }
  };

  const handleWeekendHahaOpen = () => {
    setShowWeekendHaha(true);
    setShowTools(false);
    if (socket) {
      socket.emit('client:open-weekend-haha');
    }
  };

  // Only allow indexing on homepage (no room), prevent indexing for all game pages
  // All pages use default description (no dynamic descriptions for temporary game states)
  const shouldNoindex = !!currentRoom;

  if (isGameSpectator) {
    return (
      <>
        <SEO noindex={shouldNoindex} />
        <PageWrapper
          showLeaveButton={true}
          showEndGameButton={true}
          showToolsButton={true}
          onToolsClick={handleToolsOpen}
          toolsOpen={showTools}
        >
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20 text-center mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-3 px-2">
              {t.ui.spectatorModeTitle}
            </h2>
            <p className="text-slate-600 text-sm sm:text-base leading-relaxed px-1">
              {t.ui.spectatorModeHint}
            </p>
          </div>
        </PageWrapper>
        <ToolsPanel isOpen={showTools} onClose={() => setShowTools(false)} />
      </>
    );
  }

  // Show Wordsmith word input page
  // Only show if phase is WORDSMITH_INPUT AND Wordsmith hasn't submitted yet
  if (currentRoom && gameState && currentRoom.phase === GamePhase.WORDSMITH_INPUT && !gameState.wordsmithSubmitted) {
    if (gameType === GameType.DEADPAN) {
      const isWordsmith = currentRoom.wordsmithId === myPlayerId;
      return (
        <>
          <SEO noindex={shouldNoindex} />
          <PageWrapper 
            showLeaveButton={true} 
            showEndGameButton={true}
            showToolsButton={true}
            onToolsClick={handleToolsOpen}
            toolsOpen={showTools}
            sidePanel={deadpanSidePanel}
          >
            {isWordsmith ? (
              <WordsmithWordInput />
            ) : (
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20 text-center">
                <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-3 sm:mb-4 px-2">
                  {t.games.deadpan.wordsmithWaiting}
                </h2>
              </div>
            )}
          </PageWrapper>
          <ToolsPanel isOpen={showTools} onClose={() => setShowTools(false)} />
        </>
      );
    } else if (gameType === GameType.UNDERCOVER) {
      const isWordsmith = currentRoom.undercoverConfig?.wordsmithId === myPlayerId;
      return (
        <>
          <SEO noindex={shouldNoindex} />
          <PageWrapper 
            showLeaveButton={true} 
            showEndGameButton={true}
            showToolsButton={true}
            onToolsClick={handleToolsOpen}
            toolsOpen={showTools}
          >
            {isWordsmith ? (
              <UndercoverWordsmithWordInput />
            ) : (
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20 text-center">
                <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent mb-3 sm:mb-4 px-2">
                  {t.games.undercover.wordsmithWaiting || '等待笔耕不辍提交词汇...'}
                </h2>
              </div>
            )}
          </PageWrapper>
          <ToolsPanel isOpen={showTools} onClose={() => setShowTools(false)} />
        </>
      );
    }
  }

  // Show role reveal page after votes are confirmed
  if (currentRoom && gameState && currentRoom.phase === GamePhase.RESULTS) {
    return (
      <>
        <SEO noindex={shouldNoindex} />
        <PageWrapper 
          showLeaveButton={true} 
          showEndGameButton={true}
          showToolsButton={true}
          onToolsClick={handleToolsOpen}
          sidePanel={deadpanSidePanel}
        >
          {gameType === GameType.DEADPAN ? (
            <DeadpanRoleReveal />
          ) : gameType === GameType.BOMB_RELAY ? (
            <BombRelayResults />
          ) : gameType === GameType.SIMILAR_MINDS ? (
            <SimilarMindsResults />
          ) : gameType === GameType.ACE_ATTORNEY ? (
            <AceAttorneyReveal />
          ) : (
            <UndercoverRoleReveal />
          )}
        </PageWrapper>
        <ToolsPanel isOpen={showTools} onClose={() => setShowTools(false)} />
      </>
    );
  }

  // Show voting page for all players after prep timer
  // Wordsmith and eliminated players can also see the voting interface
  if (currentRoom && gameState && currentRoom.phase === GamePhase.FACT_INPUT) {
    const factInputContent =
      gameType === GameType.SIMILAR_MINDS ? (
        <SimilarMindsAnswerInput />
      ) : gameType === GameType.ACE_ATTORNEY ? (
        <AceAttorneyAnswers />
      ) : (
        <UndercoverGhostFactInput />
      );
    return (
      <>
        <SEO noindex={shouldNoindex} />
        <PageWrapper
          showLeaveButton={true}
          showEndGameButton={true}
          showToolsButton={true}
          onToolsClick={handleToolsOpen}
        >
          {factInputContent}
        </PageWrapper>
        <ToolsPanel isOpen={showTools} onClose={() => setShowTools(false)} />
      </>
    );
  }

  if (currentRoom && gameState && currentRoom.phase === GamePhase.VOTING) {
    return (
      <>
        <SEO noindex={shouldNoindex} />
        <PageWrapper 
          showLeaveButton={true} 
          showEndGameButton={true}
          showToolsButton={true}
          onToolsClick={handleToolsOpen}
          sidePanel={deadpanSidePanel}
        >
          {gameType === GameType.DEADPAN ? (
            <DeadpanVoting />
          ) : gameType === GameType.SIMILAR_MINDS ? (
            <SimilarMindsVoting />
          ) : (
            <UndercoverVoting />
          )}
        </PageWrapper>
        <ToolsPanel isOpen={showTools} onClose={() => setShowTools(false)} />
      </>
    );
  }

  // If game has started and roles are being assigned, show role assignment screen
  if (currentRoom && gameState && currentRoom.phase === GamePhase.ROLE_ASSIGNMENT) {
    return (
      <>
        <SEO noindex={shouldNoindex} />
        <PageWrapper 
          showLeaveButton={true} 
          showEndGameButton={true}
          showToolsButton={true}
          onToolsClick={handleToolsOpen}
          sidePanel={deadpanSidePanel}
        >
          {gameType === GameType.DEADPAN ? (
            <DeadpanRoleAssignment />
          ) : gameType === GameType.BOMB_RELAY ? (
            <BombRelayPlay />
          ) : gameType === GameType.PENSIEVE ? (
            <PensieveGame />
          ) : gameType === GameType.SIMILAR_MINDS ? (
            <SimilarMindsHostPrompt />
          ) : gameType === GameType.ACE_ATTORNEY ? (
            <AceAttorneyPrompt />
          ) : isNashSchrodinger(currentRoom) ? (
            <SchrodingerCrocGame />
          ) : isNashStandApart(currentRoom) ? (
            <StandApartGame />
          ) : gameType === GameType.GAME_THEORY_WHEEL ? (
            <GameTheoryWheelGame />
          ) : (
            <UndercoverRoleAssignment />
          )}
        </PageWrapper>
        <ToolsPanel isOpen={showTools} onClose={() => setShowTools(false)} />
      </>
    );
  }

  // If in a room, show lobby
  if (currentRoom) {
    return (
      <>
        <SEO noindex={shouldNoindex} />
        <PageWrapper 
          headerAccessory={<RoomHostActionsMenu />}
          showToolsButton={true}
          onToolsClick={handleToolsOpen}
          toolsOpen={showTools}
          sidePanel={deadpanSidePanel}
        >
          <RoomLobby />
        </PageWrapper>
        <ToolsPanel isOpen={showTools} onClose={() => setShowTools(false)} />
      </>
    );
  }

  // Otherwise show create/join options
  const showInviteJoinModal = !!inviteRoomCode && !currentRoom;
  return (
    <>
      <SEO noindex={shouldNoindex} />
      <div
        className={
          showInviteJoinModal
            ? 'pointer-events-none select-none blur-[3px] transition-[filter]'
            : ''
        }
      >
        <PageWrapper 
          gradient="lighter" 
          showToolsButton={true}
          onToolsClick={handleToolsOpen}
          showWeekendHahaButton={true}
          onWeekendHahaClick={handleWeekendHahaOpen}
          toolsOpen={showTools}
          weekendHahaOpen={showWeekendHaha}
        >
          <div className="w-full max-w-4xl">
            <div className="text-center mb-6 sm:mb-10">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-2 sm:mb-3 drop-shadow-sm px-2">
                {t.ui.gameTitle}
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-2 px-2">{t.ui.gameDescription}</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
              <RoomCreation />
              <RoomJoining />
            </div>
          </div>
        </PageWrapper>
      </div>
      {showInviteJoinModal && inviteRoomCode && (
        <InviteJoinModal
          roomCode={inviteRoomCode}
          onClose={dismissInviteModal}
          onJoined={() => setInviteRoomCode(null)}
        />
      )}
      <ToolsPanel isOpen={showTools} onClose={() => setShowTools(false)} />
      <WeekendHahaPanel isOpen={showWeekendHaha} onClose={() => setShowWeekendHaha(false)} />
    </>
  );
}

function RoomOverlays() {
  const { currentRoom } = useSocket();
  if (!currentRoom) return null;
  return (
    <>
      <VoiceChatSection />
      <EmojiReactionSection />
    </>
  );
}

function App() {
  return (
    <SocketProvider>
      <AppContent />
      <RoomOverlays />
    </SocketProvider>
  );
}

export default App;

